import { ProductProps } from '@/types/Product';
import { ActivePromotion, ProductDiscount } from '@/types/Promotion';

/** First tag if the campaign has one, otherwise the discount text (e.g. "-20%"). */
function getBadgeText(rule: ActivePromotion, discountText: string): string {
  const firstTag = rule.tags?.split(',')[0]?.trim();
  return firstTag ? firstTag : discountText;
}

/**
 * Explains why the badge might not apply yet (minimum cart value) so
 * customers aren't misled into thinking the discount is unconditional.
 * Uses the campaign's own description if set, otherwise a generated hint.
 * Returns null when the campaign has no minimum_value at all.
 */
export function getMinimumValueHint(rule: ActivePromotion): string | null {
  if (!rule.minimum_value) return null;

  if (rule.description && rule.description.trim()) {
    return rule.description.trim();
  }

  const formattedMin = new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
  }).format(rule.minimum_value);

  return `Válido em compras acima de ${formattedMin}`;
}

/**
 * Preço final de uma opção com a campanha aplicada — o mesmo que o
 * checkout cobra (ApplyCartDiscountsService): o preço-alvo aplica-se mesmo
 * quando fica acima do preço original. Usado nos dados estruturados, onde o
 * Google tem de ver o preço que o cliente realmente paga.
 */
export function applyPromotionToPrice(
  rule: ActivePromotion,
  priceTax: number,
): number {
  if (rule.discount_type === 'percent' && rule.discount_percent != null) {
    return priceTax * (1 - rule.discount_percent / 100);
  }
  if (rule.discount_type === 'fixed' && rule.discount_value != null) {
    return Math.max(0, priceTax - rule.discount_value);
  }
  if (rule.discount_type === 'target_price' && rule.discount_value != null) {
    return rule.discount_value;
  }
  return priceTax;
}

/** Returns the base price for a product (minimum across all variants). */
export function getProductBasePrice(product: ProductProps): number | null {
  if (product.product_type === 'SIMPLE' && product.simple)
    return product.simple.price_tax;
  if (product.product_type === 'VARIATION' && product.variations?.length)
    return Math.min(...product.variations.map((v) => v.price_tax));
  if (product.product_type === 'COLOR_SIZE' && product.colors?.length) {
    const prices = product.colors.flatMap((c) => c.sizes.map((s) => s.price_tax));
    return prices.length ? Math.min(...prices) : null;
  }
  if (product.product_type === 'CONTACT_LENSES' && product.contact_lenses)
    return product.contact_lenses.price_tax;
  return null;
}

/**
 * Finds the best active direct-discount rule for a product.
 * Rules are already sorted by priority (asc) by the backend.
 */
export function getProductDiscount(
  product: ProductProps,
  rules: ActivePromotion[],
): ProductDiscount | null {
  // Produtos "brevemente" não têm preço público — nem campanhas aplicáveis.
  if (product.coming_soon) return null;

  const basePrice = getProductBasePrice(product);
  if (basePrice == null || rules.length === 0) return null;

  const match = rules.find((rule) => {
    // Regras só de envio grátis (sem discount_type) não devem "ganhar" a
    // procura por um desconto de preço a mostrar — isso esconderia a tag de
    // uma regra de desconto real com prioridade mais baixa.
    if (!rule.discount_type) return false;

    // Excluded products
    if (rule.cart_rule_exclude_products.some((ep) => ep.product_id === product.id))
      return false;

    // Brand targeting (empty = all brands)
    if (
      rule.cart_rule_brands.length > 0 &&
      !rule.cart_rule_brands.some((b) => b.brand_id === product.brand_id)
    )
      return false;

    // Category targeting (empty = all categories)
    if (
      rule.cart_rule_categories.length > 0 &&
      !rule.cart_rule_categories.some((c) =>
        product.categories.some((pc) => pc.category_id === c.category_id),
      )
    )
      return false;

    // Product targeting (empty = all products)
    if (
      rule.cart_rule_products.length > 0 &&
      !rule.cart_rule_products.some((p) => p.product_id === product.id)
    )
      return false;

    return true;
  });

  if (!match) return null;

  let discountedPrice: number;
  let discountText: string;

  if (match.discount_type === 'percent' && match.discount_percent != null) {
    discountedPrice = basePrice * (1 - match.discount_percent / 100);
    discountText = `-${match.discount_percent}%`;
  } else if (match.discount_type === 'fixed' && match.discount_value != null) {
    discountedPrice = Math.max(0, basePrice - match.discount_value);
    discountText = `-${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(match.discount_value)}`;
  } else if (match.discount_type === 'target_price' && match.discount_value != null) {
    if (match.discount_value < basePrice) {
      discountedPrice = match.discount_value;
      const percent = Math.round(
        ((basePrice - match.discount_value) / basePrice) * 100,
      );
      discountText = `-${percent}%`;
    } else {
      // Já está no valor-alvo ou abaixo: o preço passa a ser o valor-alvo
      // (mesmo que suba), mas sem mostrar preço riscado nem percentagem —
      // não é apresentado como desconto.
      return {
        rule: match,
        discountedPrice: match.discount_value,
        badge: getBadgeText(match, match.label),
        color: match.color || '#DC3232',
        label: match.label,
        hasPriceReduction: false,
      };
    }
  } else {
    return null;
  }

  return {
    rule: match,
    discountedPrice,
    badge: getBadgeText(match, discountText),
    color: match.color || '#DC3232',
    label: match.label,
    hasPriceReduction: true,
  };
}

/**
 * Finds the best matching discount for a cart item using only product_id.
 * Rules with brand/category targeting are skipped (not verifiable from cart data).
 * `priceTax` is the unit price including tax.
 */
export function getCartItemDiscount(
  productId: string,
  priceTax: number,
  rules: ActivePromotion[],
): ProductDiscount | null {
  if (rules.length === 0 || priceTax <= 0) return null;

  const match = rules.find((rule) => {
    if (!rule.discount_type) return false;
    if (rule.cart_rule_brands.length > 0 || rule.cart_rule_categories.length > 0) return false;
    if (rule.cart_rule_exclude_products.some((ep) => ep.product_id === productId)) return false;
    if (
      rule.cart_rule_products.length > 0 &&
      !rule.cart_rule_products.some((p) => p.product_id === productId)
    )
      return false;
    return true;
  });

  if (!match) return null;

  let discountedPrice: number;
  let discountText: string;

  if (match.discount_type === 'percent' && match.discount_percent != null) {
    discountedPrice = priceTax * (1 - match.discount_percent / 100);
    discountText = `-${match.discount_percent}%`;
  } else if (match.discount_type === 'fixed' && match.discount_value != null) {
    discountedPrice = Math.max(0, priceTax - match.discount_value);
    discountText = `-${new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(match.discount_value)}`;
  } else if (match.discount_type === 'target_price' && match.discount_value != null) {
    if (match.discount_value < priceTax) {
      discountedPrice = match.discount_value;
      const percent = Math.round(
        ((priceTax - match.discount_value) / priceTax) * 100,
      );
      discountText = `-${percent}%`;
    } else {
      return {
        rule: match,
        discountedPrice: match.discount_value,
        badge: getBadgeText(match, match.label),
        color: match.color || '#DC3232',
        label: match.label,
        hasPriceReduction: false,
      };
    }
  } else {
    return null;
  }

  return {
    rule: match,
    discountedPrice,
    badge: getBadgeText(match, discountText),
    color: match.color || '#DC3232',
    label: match.label,
    hasPriceReduction: true,
  };
}
